/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash.debounce';
import { Check, Home } from 'lucide-react';
import { generateSlug } from 'random-word-slugs';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import * as z from 'zod';

import { Story } from './LiveAgentTypes';

import { TestSuite } from '@/commons/protocols';
import { Button } from '@/components/atoms/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { useGet, useMutate } from '@/net/commons';
import { cn } from '@/utils';

const storyRequestSchema = z.object({
    goal: z.string().min(1, { message: 'The agent needs a non empty goal.' }),
    entryHref: z.string().url({ message: 'The URL format is invalid. Make sure to enter a valid URL.' }),
    instructions: z.string().min(1, { message: 'A test case is required.' }),
});

// Helper function to sanitize URLs
const sanitizeUrl = (s: string) => s.trim().replaceAll('https://https://', 'https://');

const DEFAULT_INSTRUCTIONS = `
### Prerequisites

- A separate account, with permissions and ...

### Test steps

- Login
- Create a new ...
`;

export type StoryRequestSchema = z.infer<typeof storyRequestSchema>;

export const StoryRequest = () => {
    const { projectSlug, suiteSlug } = useParams();

    const { data: suiteSettings } = useGet<TestSuite>(`/bff/project/${projectSlug}/suite/${suiteSlug}/settings`);

    const slug = `new-test-${generateSlug()}`;
    const formDefaultValue = {
        goal: slug,
        entryHref: 'https://',
        instructions: '',
    };

    if (suiteSettings?.defaultEntryHref) {
        formDefaultValue.entryHref = suiteSettings.defaultEntryHref;
    }

    const storyRequestForm = useForm<StoryRequestSchema>({
        defaultValues: formDefaultValue,
        resolver: zodResolver(storyRequestSchema),
        mode: 'onChange',
    });

    const storyGoal = storyRequestForm.watch('goal');

    useEffect(() => {
        if (storyGoal) {
            document.title = `${storyGoal} | Heal.dev`;
        }
    }, [storyGoal]);

    useEffect(() => {
        if (suiteSettings?.defaultEntryHref) {
            storyRequestForm.setValue('entryHref', suiteSettings.defaultEntryHref);
        }
    }, [suiteSettings]);

    const watchUrl = storyRequestForm.watch('entryHref');

    useEffect(() => {
        storyRequestForm.setValue('entryHref', sanitizeUrl(watchUrl ?? ''));
    }, [watchUrl]);

    const navigate = useNavigate();

    const { control } = storyRequestForm;

    const { trigger: triggerCreate } = useMutate<Story>(
        () => {
            return `/bff/project/${projectSlug}/suite/${suiteSlug}/story`;
        },
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const publish = async (asBlock?: boolean) => {
        if (!storyRequestForm.getValues().goal || storyRequestForm.getValues().goal.length === 0) {
            toast({
                title: 'Please provide a name for the story',
                variant: 'destructive',
            });
            return;
        }

        const { goal, entryHref, instructions } = storyRequestForm.getValues();
        const name = goal;

        const story = {
            name,
            kind: 'FUNCTIONAL',
            steps: [],
            variables: {},
            browsers: {
                names: ['chromium'],
                viewport: {
                    width: 1280,
                    height: 720,
                },
            },
            entryHref,
            block: asBlock,
            ...(instructions ? { storyInstructions: instructions } : {}),
        };

        const res = await triggerCreate(story);
        if (!res) return;
        toast({
            title: `Your story has been added. It'll be generated and reviewed by a Heal team member soon.`,
            variant: 'default',
        });
        const redirectUrl = `/suite/${projectSlug}/${suiteSlug}/${asBlock ? 'blocks' : 'stories'}`;
        // @ts-ignore
        navigate(`${redirectUrl}?highlightStory=${res.id}`);
    };

    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);

    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && storyRequestForm.getValues().goal && storyRequestForm.getValues().goal.length > 0
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/${encodeURIComponent(storyRequestForm.getValues().goal)}/check`
            : null,
        { method: 'GET' },
    );

    useEffect(() => {
        // Check name availability whenever the 'goal' input is set, including the default value
        if (storyRequestForm.getValues().goal && storyRequestForm.getValues().goal.length > 0) {
            setShouldCheckName(true);
        }
    }, []);

    useEffect(() => {
        if (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName, storyRequestForm.getValues().goal]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (storyRequestForm.getValues().goal && storyRequestForm.getValues().goal.length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );

    const labelClass = 'text-lead text-[hsla(222,47%,11%,1)]';
    const subLabelClass = 'text-subtle text-muted-foreground';
    const inputClass =
        'text-p-ui py-[8px] px-[12px] text-p-ui text-[hsla(222,47%,11%,1)] border border-slate-200 rounded-[6px] [text-box:trim-both] w-full';

    return (
        <div className="flex flex-col h-full overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)]">
            <Form {...storyRequestForm}>
                <div className="flex flex-row justify-between py-2 border-b mb-2 mx-4" key="info-actions">
                    <div className="text-h4 flex items-center w-full flex-wrap">
                        <label className="flex items-center">
                            <Home className="text-slate-700 me-[6px]" />
                            <span className="flex items-center gap-x-1 text-slate-500">
                                <span>Add Story</span>
                            </span>
                        </label>
                        <div className="flex gap-x-2 items-center justify-end ms-auto">
                            <Button
                                onClick={() => {
                                    publish();
                                }}
                                size="small"
                                color="purple"
                            >
                                <div className="flex items-center gap-x-2">
                                    <Check size="16" />
                                    Add Story
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="max-w-[1240px] mx-auto w-full overflow-auto">
                    <h2 className="text-h2 p-[16px]">Add Story</h2>
                    <div className="p-[16px] space-y-4">
                        <div className="space-y-2 max-w-[50%]">
                            <label className={labelClass} htmlFor="goal">
                                Story Name
                            </label>
                            <FormField
                                control={control}
                                name="goal"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                title="Story name"
                                                aria-label="Story name"
                                                id="goal"
                                                autoFocus
                                                variant="none"
                                                className={inputClass}
                                                onInput={(e) => {
                                                    setErrorMessage('');
                                                    setShouldCheckName(false);
                                                    field.onChange(e);
                                                    handleInputChange();
                                                }}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            {errorMessage && <p className="text-red-500 text-sm font-medium">{errorMessage}</p>}
                        </div>
                        <div className="space-y-2 max-w-[50%]">
                            <label className={labelClass} htmlFor="entryHref">
                                Entry URL
                            </label>
                            <FormField
                                control={control}
                                name="entryHref"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                title="Entry URL"
                                                aria-label="Entry URL"
                                                id="entryHref"
                                                variant="none"
                                                className={inputClass}
                                                onInput={(e) => {
                                                    setErrorMessage('');
                                                    setShouldCheckName(false);
                                                    field.onChange(e);
                                                    handleInputChange();
                                                }}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={labelClass} htmlFor="instructions">
                                Test Case <span className={subLabelClass}>Use markdown to annotate the test case</span>
                            </label>
                            <FormField
                                control={control}
                                name="instructions"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <textarea
                                                {...field}
                                                defaultValue={field.value ?? DEFAULT_INSTRUCTIONS}
                                                title="Test Case"
                                                aria-label="Test Case"
                                                id="instructions"
                                                className={cn(inputClass, '[field-sizing:content] min-h-[200px]')}
                                                onInput={(e) => {
                                                    setErrorMessage('');
                                                    setShouldCheckName(false);
                                                    field.onChange(e);
                                                    handleInputChange();
                                                }}
                                                rows={10}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="p-[16px]">
                        <Button
                            onClick={() => {
                                publish();
                            }}
                            size="small"
                            color="purple"
                        >
                            <div className="flex items-center gap-x-2">
                                <Check size="16" />
                                Add Story
                            </div>
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
};
